import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { TogglePanel } from '@els/els-react--toggle-panel';
import store from '@Store';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import { resultsConfig } from '@Constants/Results';
import * as Spinners from '@Services/Spinners';
import RxSpinnerComponent from '@Components/Results/utils/rx-spinner.component';
import MarkushDetails from '@Components/Results/substances/tables/markush/MarkushDetails';
import SubstanceHelper from '@Components/Results/services/substance-helper.factory';
import SubstanceQuantitativeData from '@Components/Results/substances/tables/quantitative/SubstanceQuantitativeData';
import SubstanceBioData from '@Components/Results/substances/substance-bio-data.component';
import { getValueByNs } from '@Hooks/LibraryInitialization/MoreUnderscore';
import SubstanceCommercialSupplierInformation from '@Components/Results/substances/information/commercial/SubstanceCommercialSupplierInfo';
import { contextFromMetadata } from '@Components/Results/utils/metadata';
import { getChildHitCount, getChildCount, isChildCommercialSuppliers } from '@Components/Results/substances/utils/factCountHelper';
import Catalyst from '@Services/Catalyst';
import history from '@AppRoutes/history';
import { getTrackingPathName } from '@AppRoutes/AppRoutesSelectors';
import { HIT_DATA_FACT_ID } from '@Constants';
import getSubstanceSubPropertyId from '@Components/Results/substances/SubstanceSubProperty.utils';

export default class SubstanceSubProperty extends React.PureComponent {
  constructor(props) {
    super(props);

    const {
      metadata,
      fact,
      child,
      substanceIndex,
      database: { id: databaseId },
      isDefaultOpen,
      isTabPanel,
    } = props;

    child.extendedTitle = `${history.location.pathname.indexOf('heatmap') !== -1 ? 'Heatmap:' : ''}${
      (fact && fact.title ? `${fact.title}:` : '') + child.title
    }`;

    this.state = {
      isOpen: child.open || false,
      fact,
      child,
    };

    this.id = props.panelId || getSubstanceSubPropertyId(fact, child, substanceIndex);
    this.initialItemsAmount = resultsConfig.INITIAL_ITEMS_AMOUNT.substanceQuantitative;

    this.isDefaultOpen = isDefaultOpen;
    this.isTabPanel = isTabPanel;
    this.isComponentMounted = true;

    this.toggleAccordion = this.toggleAccordion?.bind(this);
    this.retrieveFactData = SubstanceHelper.retrieveFactData.bind(this, substanceIndex, child, databaseId, metadata);
    this.trackViewedData = this.trackViewedData?.bind(this);

    this.heatmapMode = metadata.context === 'dpitems';
  }

  componentDidMount() {
    this.isComponentMounted = true;
    const { registerToggleHandler } = this.props;

    const { isOpen } = this.state;

    if (registerToggleHandler) {
      registerToggleHandler(this.toggleAccordion);
    }

    if ((this.isDefaultOpen || this.isTabPanel) && !isOpen) {
      this.toggleAccordion({ skipContentLinkTrack: true, targetOpen: true });
    }
  }

  componentWillUnmount() {
    const { registerToggleHandler } = this.props;

    if (registerToggleHandler) {
      registerToggleHandler(null);
    }

    this.isComponentMounted = false;
    Spinners.terminate(this.id);
  }

  getHeader() {
    const { child, fact } = this.state;
    const { substance } = this.props;
    const childCount = getChildCount(child, substance);

    return (
      <>
        {child.title}
        {(child.count > 0 || child.size > 0) && (
          <span>
            {' '}
            -&nbsp;
            {fact.id === HIT_DATA_FACT_ID && <span>{getChildHitCount(child)} hits out of </span>}
            {childCount}
          </span>
        )}
      </>
    );
  }

  toggleAccordion({ skipContentLinkTrack = false, targetOpen } = {}) {
    const { fact, child, isOpen } = this.state;
    const nextIsOpen = typeof targetOpen === 'boolean' ? targetOpen : !isOpen;
    const toggle = (data) => {
      if (this.isComponentMounted) {
        this.setState({ isOpen: nextIsOpen });
      }
      return data || Promise.resolve(this.state);
    };
    const trackContentLink = () => {
      const { metadata } = this.props;
      // eslint-disable-next-line no-shadow
      const { isOpen, fact, child } = this.state;
      const { title } = child;
      if (nextIsOpen && !isOpen && !skipContentLinkTrack) {
        const name = (fact?.title ? `${fact.title}:` : '') + title;

        if (metadata.isViewPreview) {
          Catalyst.trackContentLinkClick(name, `query:preview:${contextFromMetadata(metadata)}`);
        } else {
          Catalyst.trackContentLinkClick(name);
        }
      }
    };

    let actualRequest = Promise.resolve('woop woop wopp wopp'); // else case
    if (!nextIsOpen) {
      actualRequest = Promise.resolve();
    } else if (fact.type === 'bio' && !child.bioData) {
      actualRequest = this.retrieveBioData(child, fact);
    } else if (fact.type === 'fact' && child.id === 'PROD') {
      if (!child.commercialData) {
        // inner if because of collapsing logic
        actualRequest = this.initCommercialSupplierData(child, fact);
      }
    } else if (fact.type === 'fact' && !child.quanData) {
      actualRequest = this.initQuanData(child, fact);
    }
    return actualRequest.then(trackContentLink).then(toggle).catch(Logger.error);
  }

  retrieveBioData(child, fact) {
    const {
      metadata,
      database: { id: databaseId },
    } = this.props;
    const resultName = this.heatmapMode ? fact.resultName : fact.nestedResultName;
    const trackingPathName = getTrackingPathName(store.getState());
    const trackData = {
      context: metadata.context,
      pageName: trackingPathName,
      factTitle: fact.title,
      childTitle: child.title,
    };

    Spinners.activate(this.id);

    return SubstanceHelper.retrieveBioCategoryData(resultName, databaseId, child.index, trackData)
      .then((bioData) => {
        const newState = { child: { ...child, bioData } };
        const nextChild = newState.child;
        const { type, title } = nextChild;
        if (this.isComponentMounted) {
          this.setState(newState);
          this.trackViewedData(nextChild, type, title);
        }
      })
      .catch((error) => Logger.error(error))
      .finally(() => {
        Spinners.terminate(this.id);
      });
  }

  initQuanData(child) {
    if (!child.count) return Promise.resolve();

    Spinners.activate(this.id);

    return this.retrieveFactData(1, this.initialItemsAmount)
      .then((data) => {
        const newState = { child: { ...child, quanData: data } };
        const nextChild = newState.child;
        const { type, title } = nextChild;
        if (this.isComponentMounted) {
          this.setState(newState);
          this.trackViewedData(nextChild, type, title, 1, this.initialItemsAmount);
        }
      })
      .catch((error) => Logger.error(error))
      .finally(() => {
        Spinners.terminate(this.id);
      });
  }

  initCommercialSupplierData(child) {
    if (!child.count) return Promise.resolve();

    // eslint-disable-next-line no-param-reassign
    child.format = 'rcssupplier'; // will render different template and request with a different id

    Spinners.activate(this.id);

    const lastItem = child.hitData ? child.hit : child.count;

    return this.retrieveFactData(1, lastItem)
      .then((data) => {
        const newState = { child: { ...child, commercialData: data.commercialData } };
        const nextChild = newState.child;
        const { type, title } = nextChild;
        if (this.isComponentMounted) {
          this.setState(newState);
          this.trackViewedData(nextChild, type, title, 1, lastItem);
        }
      })
      .catch((error) => Logger.error(error))
      .finally(() => {
        Spinners.terminate(this.id);
      });
  }

  trackViewedData(data, type, title, firstItem, lastItem) {
    const { child } = this.state;
    const { substanceId } = this.props;
    Catalyst.trackAmountOfViewedData(
      substanceId,
      data,
      type === 'showMoreBio' ? type + child.type : type,
      title || child.title,
      store.getState(),
      firstItem,
      lastItem
    );
  }

  renderBody(isCommercialSupplierContent, shouldRenderContent = true) {
    const { child } = this.state;
    const { metadata, substance, substanceId, details, substanceIndex, commercialSupplierCount, isEmbeddedCommercialSupplier } = this.props;
    const childCount = getChildCount(child, substance);
    const hasBioData = getValueByNs(child, 'bioData.quan.data') || getValueByNs(child, 'bioData.qual.data');

    return (
      <div
        className={classnames('substance-sub-property-content', isCommercialSupplierContent ? 'rcs-commercial-accordion-content' : null)}>
        {shouldRenderContent && child.quanData && (
          <SubstanceQuantitativeData
            metadata={metadata}
            data={child.quanData}
            total={child.hitData ? child.hit : child.count}
            uniqueId={this.id}
            retrieveData={this.retrieveFactData}
            track={this.trackViewedData}
            substanceId={substanceId}
            initialItemsAmount={this.initialItemsAmount}
          />
        )}
        {shouldRenderContent && hasBioData && <SubstanceBioData metadata={metadata} fact={child} substanceId={substanceId} />}
        {shouldRenderContent && child.type === 'markush' && <MarkushDetails data={child.data} />}
        {shouldRenderContent && child.format === 'rcssupplier' && (
          <SubstanceCommercialSupplierInformation
            metadata={metadata}
            data={child.commercialData}
            details={details}
            isEmbeddedInCard={isEmbeddedCommercialSupplier}
            total={child.hitData ? child.hit : child.count}
            supplierCount={commercialSupplierCount ?? childCount}
            uniqueId={this.id}
            substanceId={substanceId}
            substanceIndex={substanceIndex}
          />
        )}
      </div>
    );
  }

  render() {
    const { isOpen, child } = this.state;
    const { isTogglePanelContent } = this.props;
    const isCommercialSupplierContent = child.format === 'rcssupplier';
    const isCommercialSupplierChild = isChildCommercialSuppliers(child) || isCommercialSupplierContent;
    const shouldRenderTogglePanel = !this.isDefaultOpen && !this.isTabPanel;

    return (
      <div
        className={classnames([
          'substance-sub-accordion-item',
          !isOpen ? 'closed' : null,
          child?.type === 'bio' ? 'no-nested-data' : null,
          this.isTabPanel ? 'substance-sub-tab-panel' : null,
          isCommercialSupplierChild ? 'substance-sub-accordion-item--commercial-supplier' : null,
        ])}>
        <RxSpinnerComponent spinnerId={this.id} position="relative" />

        {isTogglePanelContent && this.renderBody(isCommercialSupplierContent, isOpen)}

        {!isTogglePanelContent && shouldRenderTogglePanel ? (
          <TogglePanel
            id={this.id}
            data-e2e="substance-sub-property-toggle-panel"
            header={this.getHeader()}
            headerLevel="4"
            isControlled
            isOpen={isOpen}
            onClick={(panelId, openState) => this.toggleAccordion({ targetOpen: openState })}>
            {this.renderBody(isCommercialSupplierContent, isOpen)}
          </TogglePanel>
        ) : (
          !isTogglePanelContent && isOpen && this.renderBody(isCommercialSupplierContent)
        )}
      </div>
    );
  }
}

SubstanceSubProperty.propTypes = {
  metadata: MetadataPropTypesShape,
  substance: PropTypes.object.isRequired,
  child: PropTypes.object.isRequired,
  fact: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    resultName: PropTypes.string,
    nestedResultName: PropTypes.string,
    hitData: PropTypes.bool.isRequired,
    title: PropTypes.string,
  }).isRequired,
  substanceIndex: PropTypes.string.isRequired,
  database: PropTypes.object.isRequired,
  substanceId: PropTypes.string.isRequired,
  isDefaultOpen: PropTypes.bool,
  isTabPanel: PropTypes.bool,
  isTogglePanelContent: PropTypes.bool,
  isEmbeddedCommercialSupplier: PropTypes.bool,
  commercialSupplierCount: PropTypes.number,
  details: PropTypes.object,
  panelId: PropTypes.string,
  registerToggleHandler: PropTypes.func,
};
